'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { AgendaView } from '@govinity/ui/components/meetings';
import { Button } from '@govinity/ui/components/ui/button';
import { Card, CardContent } from '@govinity/ui/components/ui/card';
import { MeetingEventDocumentType } from '@govinity/validation';
import { RichTextDisplay } from '@govinity/ui/components/ui/rich-text-display';
import type { MeetingEventPublicDocument } from '@govinity/validation/public';
import { Download, FileText, Layers, type LucideIcon } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { DocumentReader } from '@/components/ui/document-reader';
import { AgendaDocumentNav } from './agenda-document-nav';
import {
  attachmentKey,
  buildAgendaNav,
  indexAgendaDocuments,
  meetingDocument,
  meetingDocumentKey,
} from './agenda-documents';
import { useDocumentDeepLink } from './lib/use-document-deep-link';
import { usePreservedScroll } from './lib/use-preserved-scroll';
import { useMeetingContext } from './meeting-context';
import { MeetingTabWrapper } from './meeting-tab-shared';
import { PortalEmptyState } from '../../components/portal-empty-state';
import { DocumentCard, useDocumentName } from '../../components/document-card';

type AgendaDocumentButtonProps = {
  doc: MeetingEventPublicDocument;
  icon: LucideIcon;
  label: string;
};

/** Opens an agenda document straight to its PDF, so it's reachable without visiting the Attachments tab. */
function AgendaDocumentButton({ doc, icon: Icon, label }: AgendaDocumentButtonProps) {
  const t = useTranslations('MeetingAgendaPage');

  return (
    <Button asChild size="sm" variant="outline" className="text-primary hover:text-primary">
      <a href={doc.signedUrl} rel="noreferrer" target="_blank">
        <Icon aria-hidden="true" />
        {label}
        <Download aria-hidden="true" />
        <span className="sr-only"> {t('opensInNewTab')}</span>
      </a>
    </Button>
  );
}

export default function MeetingAgendaPage() {
  const t = useTranslations('MeetingAgendaPage');
  const searchParams = useSearchParams();
  const searchText = searchParams.get('q') ?? undefined;
  const documentTitle = useDocumentName();
  const { meeting, agendaSections } = useMeetingContext();
  const { meetingEventDocuments: documents } = meeting;
  // Only the key is held: every meeting refetch reissues signed URLs, so the open document is
  // re-resolved from the current query data rather than frozen in state.
  const [openDocumentKey, setOpenDocumentKey] = useState<string | null>(null);
  const preserveScroll = usePreservedScroll(openDocumentKey !== null);

  const handleOpenDocument = useCallback(
    (key: string) => {
      preserveScroll();
      setOpenDocumentKey(key);
    },
    [preserveScroll]
  );

  const agendaOutlineDoc = documents.find(
    (doc) => doc.type === MeetingEventDocumentType.AGENDA_OUTLINE
  );
  const agendaPackageDoc = documents.find(
    (doc) => doc.type === MeetingEventDocumentType.AGENDA_PACKAGE
  );

  // Use agenda outline as primary agenda document. Fall back to the agenda package if it exists.
  const agendaDoc = agendaOutlineDoc ?? agendaPackageDoc;

  // Quick links to the raw PDFs so they're reachable from the structured agenda view without
  // a detour through the Attachments tab.
  const agendaDocumentActions =
    agendaOutlineDoc || agendaPackageDoc ? (
      <>
        {agendaOutlineDoc && (
          <AgendaDocumentButton
            doc={agendaOutlineDoc}
            icon={FileText}
            label={documentTitle(agendaOutlineDoc)}
          />
        )}
        {agendaPackageDoc && (
          <AgendaDocumentButton
            doc={agendaPackageDoc}
            icon={Layers}
            label={documentTitle(agendaPackageDoc)}
          />
        )}
      </>
    ) : undefined;

  const navSections = useMemo(() => buildAgendaNav(agendaSections), [agendaSections]);

  // The navigation lists the documents this tab is about; the index covers every meeting document
  // so a `?docId` deep-link from search still resolves whatever it points at.
  const agendaReaderDocuments = useMemo(
    () =>
      [agendaOutlineDoc, agendaPackageDoc]
        .filter((doc): doc is MeetingEventPublicDocument => !!doc)
        .map((doc) => meetingDocument(doc, documentTitle(doc))),
    [agendaOutlineDoc, agendaPackageDoc, documentTitle]
  );

  const documentIndex = useMemo(
    () =>
      indexAgendaDocuments(
        navSections,
        documents.map((doc) => meetingDocument(doc, documentTitle(doc)))
      ),
    [documents, documentTitle, navSections]
  );

  useEffect(() => {
    if (agendaDoc && !agendaSections.length) {
      setOpenDocumentKey(meetingDocumentKey(agendaDoc.id));
    }
  }, [agendaDoc, agendaSections.length]);

  useDocumentDeepLink(documents, (doc) => setOpenDocumentKey(meetingDocumentKey(doc.id)));

  const openDocument = openDocumentKey === null ? null : documentIndex.get(openDocumentKey);

  if (openDocument) {
    return (
      <DocumentReader
        autoRequestTranslation={openDocument.canRequestTranslation}
        document={openDocument.document}
        documentKey={openDocument.key}
        layoutStorageKey="portal-agenda-reader-layout"
        nav={
          <AgendaDocumentNav
            meetingDocuments={agendaReaderDocuments}
            navSections={navSections}
            onSelect={(document) => setOpenDocumentKey(document.key)}
            openDocumentKey={openDocument.key}
          />
        }
        onClose={() => setOpenDocumentKey(null)}
        searchText={searchText}
        title={openDocument.title}
      />
    );
  }

  if (agendaSections.length > 0) {
    return (
      <div>
        <Card>
          <CardContent>
            <MeetingTabWrapper actions={agendaDocumentActions} title={t('meetingAgendaTitle')}>
              {!!meeting.agenda?.header && (
                <RichTextDisplay className="mb-4 text-sm" content={meeting.agenda.header} />
              )}
              <AgendaView
                onAttachmentClick={(attachment) =>
                  handleOpenDocument(attachmentKey(attachment.agendaSectionItemId, attachment.id))
                }
                sections={agendaSections}
              />
              {!!meeting.agenda?.footer && (
                <RichTextDisplay className="mt-4 text-sm" content={meeting.agenda.footer} />
              )}
            </MeetingTabWrapper>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (agendaDoc) {
    return (
      <Card>
        <CardContent>
          <MeetingTabWrapper title={t('meetingAgendaTitle')}>
            <DocumentCard
              doc={agendaDoc}
              onView={() => handleOpenDocument(meetingDocumentKey(agendaDoc.id))}
              permanentLink={{ id: meeting.id, type: 'meeting' }}
            />
          </MeetingTabWrapper>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <MeetingTabWrapper title={t('meetingAgendaTitle')}>
          <PortalEmptyState
            description={t('agendaPendingDescription')}
            icon={FileText}
            title={t('agendaPendingTitle')}
          />
        </MeetingTabWrapper>
      </CardContent>
    </Card>
  );
}
